/**
 * @package   EGI / Frontend — wishlist
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-373)
 * @date      2026-07-03
 * @purpose   Entry isola wishlist (design §2): STANDALONE — tutto il contesto
 *            arriva dal blocco JSON #wishlist-config emesso dal Blade (niente
 *            #app-config). Click DELEGATO su document per [data-wishlist-toggle]
 *            (le card iniettate da load-more/carousel ereditano senza re-bind);
 *            aria-pressed riallineato on-boot e a ogni egi:wishlist:changed;
 *            badge navbar + live region unica [data-wishlist-live] (§1.2/§7);
 *            in-flight = aria-busy, MAI disabled (focus mai rubato, WCAG 2.2
 *            §2.4.7 A). API esposta: window.egiWishlist.refreshToggles.
 */

import { initCartIsland } from '../cart/cart-island';
import { initWishlistPage } from './page';
import { isAbortError } from './server-driver';
import { WishlistStore } from './store';
import {
    CHANGED_EVENT,
    MERGED_EVENT,
    isWishlistConfig,
    type WishlistChangedDetail,
    type WishlistConfig,
    type WishlistLabels,
    type WishlistMergedDetail,
} from './types';

/** Legge e valida il blocco #wishlist-config: assente/malformato → isola spenta. */
function readConfig(): WishlistConfig | null {
    const el = document.getElementById('wishlist-config');
    if (!el || !el.textContent) {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(el.textContent);
        if (isWishlistConfig(parsed)) {
            return parsed;
        }
    } catch {
        // JSON malformato → nessun errore console-rumoroso: la pagina resta SSR-funzionante.
    }
    return null;
}

function createAnnouncer(): (message: string) => void {
    let timer: number | undefined;
    return (message: string): void => {
        const region = document.querySelector<HTMLElement>('[data-wishlist-live]');
        if (!region) {
            return;
        }
        // Svuota-e-ripopola: due annunci identici consecutivi restano un change-event.
        region.textContent = '';
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
            region.textContent = message;
        }, 50);
    };
}

function updateBadge(count: number, labels: WishlistLabels): void {
    document.querySelectorAll<HTMLElement>('[data-wishlist-count]').forEach((el) => {
        el.textContent = count > 99 ? '99+' : String(count);
        // Gotcha organo: visibilità via display, MAI attributo hidden battuto dalle utility.
        el.style.display = count > 0 ? 'inline-block' : 'none';
    });
    document.querySelectorAll<HTMLElement>('[data-wishlist-nav]').forEach((link) => {
        link.setAttribute(
            'aria-label',
            count > 0 ? labels.navCount.replace(':count', String(count)) : labels.navLabel,
        );
    });
}

function boot(): void {
    const config = readConfig();
    if (config === null) {
        return;
    }

    const labels = config.labels;
    const announce = createAnnouncer();
    const store = new WishlistStore(config); // boot differito a microtask (vedi store)

    function refreshToggles(root: ParentNode = document): void {
        root.querySelectorAll<HTMLElement>('[data-wishlist-toggle]').forEach((btn) => {
            const id = btn.dataset.egiId ?? '';
            btn.setAttribute('aria-pressed', store.has(id) ? 'true' : 'false');
        });
    }

    async function onToggleClick(btn: HTMLElement): Promise<void> {
        const id = btn.dataset.egiId ?? '';
        if (id === '' || btn.getAttribute('aria-busy') === 'true') {
            return;
        }
        btn.setAttribute('aria-busy', 'true'); // mai disabled: il focus resta (design §4)
        btn.style.opacity = '0.6';
        try {
            await store.toggle({
                id,
                title: btn.dataset.egiTitle ?? '',
                imageUrl: btn.dataset.egiImage ?? '',
            });
        } catch (error) {
            if (!isAbortError(error)) {
                announce(labels.error); // rollback ARIA/visivo già fatto dallo store+changed
            }
        } finally {
            btn.removeAttribute('aria-busy');
            btn.style.opacity = '';
        }
    }

    // Click delegato su document (contratto isole EGI: card iniettate ereditano).
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const btn = target.closest<HTMLElement>('[data-wishlist-toggle]');
        if (btn) {
            event.preventDefault();
            void onToggleClick(btn);
        }
    });

    document.addEventListener(CHANGED_EVENT, (event) => {
        const detail = (event as CustomEvent<WishlistChangedDetail>).detail;
        updateBadge(detail.count, labels);
        refreshToggles();
        // Annunci centralizzati QUI (unica sorgente — §1.2): add/remove; sync è silenzioso.
        if (detail.action === 'add') {
            announce(labels.added);
        } else if (detail.action === 'remove') {
            announce(labels.removed);
        }
    });

    document.addEventListener(MERGED_EVENT, (event) => {
        const detail = (event as CustomEvent<WishlistMergedDetail>).detail;
        updateBadge(detail.count, labels);
        announce(detail.skipped.length > 0 ? labels.merged + ' ' + labels.mergeSkipped : labels.merged);
    });

    initWishlistPage(store, labels, announce);

    // M-EGI-385 — modulo carrello: STESSA isola (ADR-C6), stessa live region
    // (decisione M-373), resync dello store dopo ogni move cross-lista.
    initCartIsland(config.cart, {
        authenticated: config.authenticated,
        announce,
        wishlistResync: () => store.resync(),
    });

    window.egiWishlist = { refreshToggles };
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
    boot();
}

export {};
